import { normalizeEntity, validateSnapshot } from './contract';

function list(value, label) {
  if (!Array.isArray(value)) throw new Error(`Resposta incompleta: ${label}`);
  return value;
}

// Limit requests for product sizes; the current API has no complete batch route.
async function mapLimited(items, mapper, concurrency = 6) {
  const result = new Array(items.length);
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (next < items.length) {
      const index = next++;
      result[index] = await mapper(items[index]);
    }
  }));
  return result;
}

export async function loadCompanySnapshot(api, session) {
  const { companyId, token } = session;
  const get = async (path) => (await api.get(path, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000,
  })).data;
  const [company, configuration, regularGroups, additionalGroups, rawProducts, sizeDescriptions,
    paymentLinks, rawPayments, rawSchedules] = await Promise.all([
    get(`empresa/encontrar/${companyId}`),
    get(`chatbot/find/${companyId}`),
    get(`grupo-produto/listarGestor/${companyId}`),
    get(`grupo-produto/listarAdicionaisTodos/${companyId}`),
    get(`produto/listarGestor/${companyId}`),
    get(`tamanho/listar-empresa/${companyId}`),
    get(`forma-pagamento-empresa/listarGestor/${companyId}`),
    get('formas-pagamento/listar'),
    get(`horario/listarGestor/${companyId}`),
  ]);
  if (!configuration?.chatbot || Array.isArray(configuration.chatbot)) {
    throw new Error('A empresa ainda não possui configuração completa do chatbot');
  }
  const groups = [...list(regularGroups, 'groups'), ...list(additionalGroups, 'additionalGroups')]
    .map(normalizeEntity);
  const descriptions = new Map(list(sizeDescriptions, 'sizeDescriptions').map((size) => [String(size.Id), size]));
  const products = list(rawProducts, 'products').map(normalizeEntity)
    .filter((product) => product.Ativo && product.Disponivel && product.TipoProduto !== 'BORDA');
  const expandedProducts = await mapLimited(products, async (product) => {
    const sizes = list(await get(`produto-tamanho/listarGestor/${product.Id}`), 'sizes').map((size) => {
      const description = descriptions.get(String(size.IdTamanho));
      return normalizeEntity({ ...size, ...(description ? {
        Descricao: description.Descricao, QtdPedacos: description.QtdPedacos, Sequencia: description.Sequencia,
      } : {}) });
    });
    return { ...product, sizes };
  });
  const groupIds = new Set(groups.map((group) => String(group.Id)));
  if (expandedProducts.some((product) => !groupIds.has(String(product.Grupo)))) {
    throw new Error('Produto sem grupo: atualização concorrente ou dados inconsistentes');
  }
  const productsByGroup = new Map();
  expandedProducts.forEach((product) => {
    const key = String(product.Grupo);
    if (!productsByGroup.has(key)) productsByGroup.set(key, []);
    productsByGroup.get(key).push(product);
  });
  const paymentIds = new Set(list(paymentLinks, 'paymentLinks').map((link) => String(link.IdPagamento)));
  const availablePayments = list(rawPayments, 'paymentMethods');
  if ([...paymentIds].some((id) => !availablePayments.some((payment) => String(payment.Id) === id))) {
    throw new Error('Forma de pagamento vinculada não encontrada');
  }
  return validateSnapshot({ companyId, company: normalizeEntity(company),
    chatbot: normalizeEntity(configuration.chatbot),
    answers: list(configuration.answer, 'answers').map(normalizeEntity),
    groups: groups.map((group) => ({ ...group, products: productsByGroup.get(String(group.Id)) || [] })),
    paymentMethods: availablePayments.filter((payment) => paymentIds.has(String(payment.Id))).map(normalizeEntity),
    schedules: list(rawSchedules, 'schedules').map(normalizeEntity),
  });
}
